/* =========================================================
   ROUTINE BUNDLE (sections/routine-bundle.liquid)
   UI only. Add to cart is done by the theme's existing
   <products-bundle> element (global.js → ProductsBundle), which
   adds every child with class "bundle-product". This script:
   - keeps that class in sync with the ticked checkboxes,
   - enforces the minimum number of ticked products,
   - updates the button label / offer price / strike price.
========================================================= */
if (!customElements.get('routine-bundle')) {
  customElements.define('routine-bundle', class RoutineBundle extends HTMLElement {
    connectedCallback() {
      this.checkboxes = Array.from(this.querySelectorAll('[data-routine-checkbox]'));
      this.bundle = this.querySelector('products-bundle');
      this.button = this.querySelector('[data-routine-button]');
      this.labelEl = this.querySelector('[data-routine-label]');
      this.priceEl = this.querySelector('[data-routine-price]');
      this.compareEl = this.querySelector('[data-routine-compare]');
      this.messageEl = this.querySelector('[data-routine-message]');
      this.min = Math.max(1, parseInt(this.dataset.min, 10) || 1);
      try {
        this.offerPrices = JSON.parse(this.dataset.offerPrices || '{}');
      } catch (e) {
        this.offerPrices = {};
      }
      if (!this.bundle || !this.button) return;

      this.addEventListener('change', (e) => this.onChange(e));
      // ProductsBundle listens for clicks on the whole <products-bundle>. Stop the click
      // before it gets there when the selection is below the minimum.
      this.bundle.addEventListener('click', (e) => {
        if (this.checked().length < this.min) {
          e.stopImmediatePropagation();
          e.preventDefault();
          this.showMessage();
        }
      }, true);

      this.update();
    }

    checked() {
      return this.checkboxes.filter((c) => c.checked && !c.disabled);
    }

    onChange(e) {
      const checkbox = e.target.closest('[data-routine-checkbox]');
      if (!checkbox) return;
      if (!checkbox.checked && this.checked().length < this.min) {
        checkbox.checked = true;
        this.showMessage();
        return;
      }
      this.hideMessage();
      this.update();
    }

    update() {
      const checked = this.checked();
      const ids = checked.map((c) => c.dataset.variantId);

      // Only ticked products keep the class ProductsBundle reads.
      this.querySelectorAll('[data-routine-item]').forEach((item) => {
        item.classList.toggle('bundle-product', ids.includes(item.dataset.routineItem));
      });
      this.checkboxes.forEach((c) => c.closest('.routine-bundle__card')?.classList.toggle('is-checked', c.checked));

      const count = checked.length;
      const total = checked.reduce((sum, c) => sum + (parseInt(c.dataset.price, 10) || 0), 0);
      const offer = (parseFloat(this.offerPrices[String(count)]) || 0) * 100;
      const hasOffer = offer > 0 && offer < total;

      this.labelEl.textContent = (this.dataset.textButton || 'Add any [count]').replace('[count]', count);
      this.priceEl.textContent = count ? ' · ' + this.formatMoney(hasOffer ? offer : total) : '';
      this.compareEl.textContent = hasOffer ? this.formatMoney(total) : '';

      // Add button's CleverTap item_title (read by the global .section-item-tracked
      // handler in ga4-dataLayer.liquid) = the products that will be added.
      this.button.setAttribute('data-item-title', checked.map((c) => c.getAttribute('data-item-title') || '').join(' | '));

      const valid = count >= this.min;
      this.button.disabled = !valid;
      this.bundle.classList.toggle('is-disabled', !valid);
      this.bundle.setAttribute('aria-disabled', valid ? 'false' : 'true');
    }

    showMessage() {
      if (!this.messageEl) return;
      this.messageEl.textContent = (this.dataset.textMin || '').replace('[min]', this.min);
      clearTimeout(this.messageTimer);
      this.messageTimer = setTimeout(() => this.hideMessage(), 3000);
    }

    hideMessage() {
      if (this.messageEl) this.messageEl.textContent = '';
    }

    formatMoney(cents) {
      const format = this.dataset.moneyFormat || '₹{{amount_no_decimals}}';
      const withDelimiters = (precision, thousands = ',', decimal = '.') => {
        const [whole, fraction] = (cents / 100).toFixed(precision).split('.');
        const w = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
        return fraction ? w + decimal + fraction : w;
      };
      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
        switch (key) {
          case 'amount_with_comma_separator': return withDelimiters(2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return withDelimiters(0, '.');
          case 'amount_no_decimals': return withDelimiters(0);
          default: return cents % 100 === 0 ? withDelimiters(0) : withDelimiters(2);
        }
      }).replace(/<[^>]*>/g, '');
    }
  });
}
